import React from 'react';
import { AlertCircle, Eraser, Link2, MapPin, Route as RouteIcon, Settings2 } from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import MapArea from './MapArea';
import { cn } from '../../lib/cn';
import { getRouteDisplayName } from '../../utils/routeManagementHelpers';
import { useAuth } from '../../hooks/useAuth';

export default function MapPage({ controller }) {
  const {
    clearMapSelection,
    openCreateStopForm,
    resolveStopMasterName,
    savedRoutes,
    viewingRoute,
    viewingRouteIds,
    visibleStopIds
  } = controller;

  const { isEditor } = useAuth();

  const extraRoutes = React.useMemo(
    () => savedRoutes.filter((r) => viewingRouteIds && viewingRouteIds.has(r._id)),
    [savedRoutes, viewingRouteIds]
  );

  const stopsCount = visibleStopIds.size;
  const routesCount = (viewingRoute ? 1 : 0) + extraRoutes.length;
  const isEmpty = stopsCount === 0 && routesCount === 0;

  return (
    <div className="flex-1 min-w-0 min-h-0 px-6 lg:px-8 py-6 overflow-hidden flex flex-col">
      <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-xl shadow-soft flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-4 flex-wrap px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-600">
              Map workspace
            </p>
            <h2 className="text-[20px] font-semibold tracking-tight text-slate-900 mt-1">
              Selected routes and stops
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 max-w-xl">
              Use the dashboard or the stops workspace to load only the routes or stops you want to inspect here.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <CountBadge icon={MapPin} value={stopsCount} />
            <CountBadge icon={RouteIcon} value={routesCount} tone="brand" />
            {isEditor && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={Settings2}
                onClick={openCreateStopForm}
              >
                Manage stops
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={Eraser}
              onClick={clearMapSelection}
              disabled={isEmpty}
            >
              Clear map
            </Button>
          </div>
        </div>

        {isEmpty && (
          <div className="mx-5 mt-4 mb-1 flex items-center gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-[12.5px] text-amber-900">
              Select one or more stops or routes from the data table to load them on the map.
            </p>
          </div>
        )}

        {!isEmpty && (
          <SelectionSummary
            viewingRoute={viewingRoute}
            extraRoutes={extraRoutes}
            stopsCount={stopsCount}
            resolveStopMasterName={resolveStopMasterName}
          />
        )}

        <div className="relative flex-1 min-h-0 m-5 mt-3 rounded-lg overflow-hidden border border-slate-200">
          <MapArea controller={controller} />
        </div>
      </div>
    </div>
  );
}

function CountBadge({ icon: Icon, value, tone = 'default' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2 rounded-md border text-[12px] font-semibold tabular-nums',
        tone === 'brand'
          ? 'border-brand-200 bg-brand-50 text-brand-700'
          : 'border-slate-200 bg-white text-slate-700'
      )}
    >
      <Icon className={cn('h-3.5 w-3.5', tone === 'brand' ? 'text-brand-600' : 'text-slate-500')} />
      <span>{value}</span>
    </span>
  );
}

function SelectionSummary({ viewingRoute, extraRoutes, stopsCount, resolveStopMasterName }) {
  if (!viewingRoute && extraRoutes.length === 0 && stopsCount === 0) return null;

  return (
    <div className="mx-5 mt-4 mb-1 flex flex-wrap items-center gap-2">
      {viewingRoute && (
        <Badge variant="brand" size="md" dot>
          Primary: {getRouteDisplayName(viewingRoute, resolveStopMasterName)}
        </Badge>
      )}
      {extraRoutes.map((route) => (
        <Badge key={route._id} variant="default" size="md" dot>
          {getRouteDisplayName(route, resolveStopMasterName)}
        </Badge>
      ))}
      {stopsCount > 0 && !viewingRoute && extraRoutes.length === 0 && (
        <Badge variant="info" size="md" dot>
          <Link2 className="h-3 w-3 mr-1" />
          {stopsCount} stop{stopsCount === 1 ? '' : 's'} on map
        </Badge>
      )}
    </div>
  );
}
